import { Link } from "react-router-dom";
import { ArrowRight, Hotel, Anchor, Building2, ChefHat } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import Reveal from "@/components/Reveal";
import GoldCorners from "@/components/GoldCorners";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_web-essentials-21/artifacts/gevxys3s_IMG_2236.png";
const KITCHEN_IMG =
  "https://customer-assets.emergentagent.com/job_web-essentials-21/artifacts/c7itkshs_9D73C893-6699-46EE-A6BA-C73A4AAB96A3.png";
const PLATE_IMG =
  "https://customer-assets.emergentagent.com/job_web-essentials-21/artifacts/x8bsamln_IMG_2103.png";

const PROP_ICONS = [Hotel, Building2, Anchor, ChefHat];

function StagedImage({ src, alt }) {
  return (
    <div className="relative">
      <div
        aria-hidden
        className="absolute -inset-6 sm:-inset-8 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(26, 80, 115, 0.35), rgba(15, 44, 64, 0.18) 45%, transparent 75%)",
          filter: "blur(24px)",
        }}
      />
      <div
        aria-hidden
        className="absolute -inset-4 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 70% 30%, rgba(199,162,108,0.10), transparent 55%)",
          filter: "blur(20px)",
        }}
      />
      <div
        className="relative aspect-[4/5] sm:aspect-[16/11] overflow-hidden"
        style={{
          boxShadow:
            "0 30px 60px -20px rgba(18, 60, 90, 0.55), 0 14px 28px -10px rgba(7, 14, 19, 0.6)",
        }}
      >
        <img
          src={src}
          alt={alt}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: "brightness(0.78) contrast(1.08) saturate(1.05)" }}
        />
        <div
          className="absolute inset-0 mix-blend-overlay opacity-55"
          style={{
            background:
              "linear-gradient(135deg, rgba(199,162,108,0.28) 0%, rgba(168,132,90,0.18) 45%, rgba(7,14,19,0.45) 100%)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#070E13]/75 via-[#070E13]/15 to-transparent" />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ boxShadow: "inset 0 0 120px 20px rgba(7,14,19,0.55)" }}
        />
        <GoldCorners size={14} inset={-2} />
      </div>
    </div>
  );
}

export default function Hospitality() {
  const { t } = useLang();
  const h = t.hospitality;
  return (
    <div data-testid="page-hospitality" className="bg-[#070E13] text-[#F4EEE8]">
      <section className="relative pt-40 pb-24 sm:pb-32 overflow-hidden">
        <div className="absolute inset-0 motif-grid opacity-25" />
        <div className="absolute inset-0 motif-hex opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#070E13]/40 to-[#070E13]" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <Reveal className="lg:col-span-7">
            <p className="overline gold-text">{h.eyebrow}</p>
            <h1 className="h-editorial text-5xl sm:text-6xl lg:text-7xl mt-5 leading-[1.04]">{h.title}</h1>
            <p className="mt-7 text-xl sm:text-2xl text-[#E8DFC8]/95 italic font-serif leading-snug max-w-2xl">{h.tagline}</p>
            <p className="mt-8 max-w-2xl text-lg text-[#D6D1C9] leading-relaxed">{h.lead}</p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/contact" data-testid="hospitality-primary-cta" className="cta-premium inline-flex items-center gap-2 bg-gradient-to-br from-[#E0C58E] via-[#C7A26C] to-[#A8845A] text-[#070E13] px-9 py-4 text-xs uppercase tracking-[0.24em] font-medium">
                {h.ctaPrimary} <ArrowRight size={16} />
              </Link>
              <Link to="/method" data-testid="hospitality-secondary-cta" className="inline-flex items-center gap-2 border border-[#F4EEE8]/35 text-[#F4EEE8] px-9 py-4 text-xs uppercase tracking-[0.24em] hover:border-[#C7A26C] hover:text-[#C7A26C] transition-colors">
                {h.ctaSecondary}
              </Link>
            </div>
          </Reveal>
          <Reveal delay={150} className="lg:col-span-5">
            <StagedImage src={HERO_IMG} alt="Hospitality longevity" />
          </Reveal>
        </div>
      </section>

      <section className="border-y border-[#C7A26C]/15 py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
          <div className="max-w-3xl">
            <Reveal>
              <p className="overline gold-text">{h.whereEyebrow}</p>
              <h2 className="h-editorial text-4xl sm:text-5xl mt-5">{h.whereTitle}</h2>
            </Reveal>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {h.props.map((p, i) => {
              const Icon = PROP_ICONS[i];
              return (
                <Reveal key={i} delay={(i % 2) * 80} className="relative bg-[#0C1821] border border-[#C7A26C]/20 hover:border-[#C7A26C]/55 transition-colors duration-500 p-8 lg:p-10">
                  <GoldCorners size={12} inset={-1} />
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-full border border-[#C7A26C]/55 flex items-center justify-center shrink-0">
                      <Icon size={18} className="text-[#C7A26C]" strokeWidth={1.4} />
                    </div>
                    <div>
                      <h3 className="font-serif text-2xl leading-tight">{p.t}</h3>
                      <p className="mt-3 text-[#D6D1C9]/90 leading-relaxed">{p.d}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 border-b border-[#C7A26C]/15">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14 items-start">
          <Reveal className="lg:col-span-5 lg:sticky lg:top-32">
            <p className="overline gold-text">{h.deliverEyebrow}</p>
            <h2 className="h-editorial text-4xl sm:text-5xl mt-5">{h.deliverTitle}</h2>
            <p className="mt-6 text-[#D6D1C9] leading-relaxed">{h.deliverLead}</p>
            <div className="mt-10">
              <StagedImage src={KITCHEN_IMG} alt="Hospitality kitchen integration" />
            </div>
          </Reveal>
          <div className="lg:col-span-7 space-y-4">
            {h.delivery.map(([n, ti, d], i) => (
              <Reveal key={i} delay={i * 60} className="relative bg-[#0C1821] border border-[#C7A26C]/20 hover:border-[#C7A26C]/45 transition-colors duration-500 p-7 lg:p-8">
                <GoldCorners size={10} inset={-1} />
                <div className="flex items-start gap-5">
                  <span className="font-serif text-3xl gold-text leading-none shrink-0">{n}</span>
                  <div>
                    <h3 className="font-serif text-xl">{ti}</h3>
                    <p className="mt-3 text-sm text-[#D6D1C9]/90 leading-relaxed">{d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-24 sm:py-32 overflow-hidden">
        <img src={PLATE_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#070E13]/95 via-[#070E13]/85 to-[#070E13]" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, rgba(199,162,108,0.08), transparent 60%)" }} />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <span className="block w-12 h-px bg-[#C7A26C] mx-auto mb-8" />
            <p className="font-serif text-3xl sm:text-4xl lg:text-5xl italic leading-snug">"{h.closingQuote}"</p>
            <Link to="/contact" data-testid="hospitality-bottom-cta" className="cta-premium inline-flex items-center gap-2 mt-12 bg-gradient-to-br from-[#E0C58E] via-[#C7A26C] to-[#A8845A] text-[#070E13] px-9 py-4 text-xs uppercase tracking-[0.24em] font-medium">
              {h.bottomCta} <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
